// Переключатель представлений раздела «Найм». Одно представление — одна грамматика строки
// (правило 2): кандидат, запись чёрного списка, предложение из выгрузки, событие сверки,
// учётная запись площадки, переписка, формулировка помощника.
//
// В переключателе стоят ТОЛЬКО те представления, до чьего права человек дотянулся. Причина не
// в косметике: раздача прав найма разная у всех пяти ролей (финотдел видит один чёрный список,
// администратор — площадку и помощника), и вкладка, за которой ждёт отказ, — это приглашение
// в тупик. Изоляция при этом живёт на сервере выборкой, здесь только навигация.
//
// ## Почему полосой вкладок, а не сегментированным переключателем — и почему во всю ширину
//
// Правка 03.09.2026 по кадру живого экрана. До неё переключатель был сегментированным и стоял
// ВНУТРИ колонки списка. Сегментированный делит доступную ширину на равные доли и запрещает
// перенос текста; восемь представлений в колонке 416 точек дают долю в 52 точки, и все восемь
// названий рисуются друг поверх друга — на месте навигации сплошная каша. Видно было на
// `кабинет-найм:-кандидаты.png`, и это единственный экран продукта, где навигацию нельзя было
// прочитать вовсе.
//
// Доказательство причины лежало рядом: на полноширинных представлениях («Воронка», «Сверки»,
// «Библиотека») тот же переключатель растягивался на весь экран и читался безупречно. То есть
// ломал его не компонент, а узкое место.
//
// Отсюда две правки сразу, и обе обязательные:
//
// 1. **Переключатель поднят на уровень раздела** и стоит над обеими колонками, как в «Расчёте».
//    Побочная польза важнее косметики: раньше он ПРЫГАЛ — в списочных представлениях сидел в
//    колонке слева, а в полноширинных уезжал наверх во всю ширину. Навигация, меняющая место
//    при переходе между своими же вкладками, — это отдельный способ потерять человека.
// 2. **Взята полоса вкладок, а не сегментированный.** У неё ширина по содержимому, перенос
//    запрещён, а обёртка прокручивается: наложение названий друг на друга невозможно ПО
//    УСТРОЙСТВУ, а не по расчёту, что «восемь коротких слов поместятся». Восемь — это уже
//    впритык на ноутбуке, а девятое представление добавят через месяц.
//
// Граница, чтобы два вида переключателя не расползлись по продукту: до пяти представлений —
// сегментированный (так в «Деньгах», «Людях», «Объектах», «Расчёте»), шесть и больше — полоса
// вкладок. Держит эту границу `web/src/test/оформление.test.ts`.

import { useNavigate } from 'react-router';

import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import type { HiringRoute, HiringView } from '../route';
import { HIRING_VIEWS, VIEW_PERMISSION, VIEW_TITLES, isHiringView, switchViewHref } from '../route';

/** Представления, доступные с этим набором прав, в порядке объявления. */
export function allowedViews(permissions: readonly string[]): HiringView[] {
  return HIRING_VIEWS.filter((view) => can(permissions, VIEW_PERMISSION[view]));
}

export function ViewSwitcher({ route }: { route: HiringRoute }) {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);

  const views = allowedViews(permissions);
  // Переключатель, в котором нечего переключать, не показывается вовсе: у финансового отдела
  // в разделе ровно три представления чёрного списка, а одна кнопка «Чёрный список» над
  // списком чёрного списка ничего не сообщает и только съедает строку.
  if (views.length < 2) {
    return null;
  }

  // Открытое по прямой ссылке представление, которого нет в списке доступных, обязано
  // остаться в переключателе: иначе человек видит экран, а активной вкладки нет.
  const items = views.includes(route.view) ? views : [...views, route.view];

  return (
    <TabMenu.Root
      value={route.view}
      onValueChange={(next) => {
        if (isHiringView(next) && next !== route.view) {
          void navigate(switchViewHref(next, route.search));
        }
      }}
    >
      {/* Верхней линии у полосы нет: её уже несёт нижняя граница шапки раздела, и вместе
          получалась бы двойная. Отступ по горизонтали тот же, что у полосы «Расчёта». */}
      {/* На узком экране полоса прокручивается вбок, а не уезжает за край: восемь
          представлений найма в 744 точки не помещаются никак, и без прокрутки последние
          три просто переставали существовать. */}
      <TabMenu.List className='h-11 gap-6 border-t-0 px-5 тесно:gap-4 узко:overflow-x-auto узко:px-4'>
        {items.map((view) => (
          <TabMenu.Trigger key={view} value={view}>
            {VIEW_TITLES[view]}
          </TabMenu.Trigger>
        ))}
      </TabMenu.List>
    </TabMenu.Root>
  );
}
